import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';

import type { ResourceIdDto } from '@/api/generated';

import { useApiClient } from '@/api/clients/useApiClient.util.ts';
import { QueryKeyEnum } from '@/api/model/QueryKey.enum.ts';
import { useUserContext } from '@/contexts/userContext/User.context.ts';

type Req = {
  externalDiskId: string;
  externalFileId: string;
  resource: ResourceIdDto;
};

type Return = {
  deleteFile: (req: Req) => Promise<void>;
  isDeleting: boolean;
};

export const useDeleteFile = (): Return => {
  const { attachmentsApi } = useApiClient();
  const queryClient = useQueryClient();
  const { userId } = useUserContext();

  const { isPending, mutateAsync } = useMutation<void, AxiosError, Req>({
    mutationFn: async req => {
      const { data } = await attachmentsApi.deleteAttachmentFile(
        userId,
        req.resource.resourceId,
        req.resource.type,
        req.externalFileId,
        req.externalDiskId,
      );
      return data;
    },
    onSuccess: async (_data, req) => {
      await queryClient.refetchQueries({
        queryKey: [QueryKeyEnum.FILES, req.resource.resourceId, req.resource.type],
      });
    },
  });

  return {
    deleteFile: mutateAsync,
    isDeleting: isPending,
  };
};
